import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { notify } from '../components/BalmartToaster';

const CartContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let sid = localStorage.getItem('cart_session_id');
  if (!sid) {
    sid = 'sess_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('cart_session_id', sid);
  }
  return sid;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const sessionId = getSessionId();

  const fetchCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart/${sessionId}`);
      setItems(res.data);
    } catch (e) {
      console.error('Failed to fetch cart', e);
    }
  }, [sessionId]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const addToCart = async (productId, quantity = 1) => {
    try {
      const res = await axios.post(`${API}/cart/${sessionId}`, { product_id: productId, quantity });
      setItems(res.data);
      setIsOpen(true);
      notify.success('Ajouté au panier');
    } catch (e) {
      console.error('Failed to add to cart', e);
      notify.error("Impossible d'ajouter au panier");
    }
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      const res = await axios.put(`${API}/cart/${sessionId}/${productId}`, { product_id: productId, quantity });
      setItems(res.data);
    } catch (e) {
      console.error('Failed to update cart', e);
    }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${API}/cart/${sessionId}/${productId}`);
      setItems(items.filter(i => i.product_id !== productId));
    } catch (e) {
      console.error('Failed to remove item', e);
    }
  };

  const clearCart = async () => {
    try {
      await axios.delete(`${API}/cart/${sessionId}`);
      setItems([]);
    } catch (e) {
      console.error('Failed to clear cart', e);
    }
  };

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => {
    const price = i.product?.price || 0;
    return sum + price * i.quantity;
  }, 0);

  return (
    <CartContext.Provider value={{ items, totalItems, totalPrice, addToCart, updateQuantity, removeItem, clearCart, isOpen, setIsOpen, sessionId, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
